import {
  COURSE_WIDTH,
  FINISH_CLEAR,
  MAX_SPEED,
  PLAYER_RADIUS,
  RAMP_DISTANCE,
  START_PAD,
  STEER_BASE,
  STEER_SPEED_GAIN,
  TRACK_LENGTH,
} from "./constants";
import { getObject } from "./objects";
import { chance, pick, randInt, randRange, type Rng } from "./rng";

export type Entity = {
  uid: number;
  defId: string;
  x: number;
  y: number;
  variant: number;
  consumed: boolean;
  ignoreUntil: number;
};

export type Scenery = {
  x: number;
  y: number;
  sprite: string;
  scale: number;
  flip: boolean;
};

export type Course = {
  entities: Entity[];
  scenery: Scenery[];
  length: number;
  width: number;
};

type Placement = { defId: string; x: number; y: number; variant?: number };

let nextUid = 1;

const MIN_GAP = PLAYER_RADIUS * 2 + 64;
const EDGE = 38;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function occupiedIntervals(placements: Placement[], y: number, band: number): [number, number][] {
  const intervals: [number, number][] = [];
  for (const p of placements) {
    if (Math.abs(p.y - y) > band) continue;
    const def = getObject(p.defId);
    // Only solids close the lane. This is an allow-list on purpose: naming the
    // types to skip meant every new non-blocking type was silently counted as
    // an obstacle here, which narrows the guaranteed gap without narrowing
    // anything the player can actually hit.
    if (def.collision !== "solid") continue;
    const hw = def.hitbox.w / 2;
    intervals.push([p.x - hw, p.x + hw]);
  }
  intervals.sort((a, b) => a[0] - b[0]);
  return intervals;
}

function widestGap(intervals: [number, number][], width: number): [number, number] {
  let best: [number, number] = [EDGE, width - EDGE];
  let bestW = -Infinity;
  let cursor = EDGE;
  const consider = (l: number, r: number) => {
    if (r - l > bestW) {
      bestW = r - l;
      best = [l, r];
    }
  };
  for (const [l, r] of intervals) {
    consider(cursor, l);
    cursor = Math.max(cursor, r);
  }
  consider(cursor, width - EDGE);
  return best;
}

function largestGap(intervals: [number, number][], width: number): number {
  const [l, r] = widestGap(intervals, width);
  return Math.max(0, r - l);
}

function canPlace(placements: Placement[], x: number, y: number, defId: string): boolean {
  const def = getObject(defId);
  const hw = def.hitbox.w / 2 + 8;
  const hh = def.hitbox.h / 2 + 10;
  if (x - hw < EDGE || x + hw > COURSE_WIDTH - EDGE) return false;
  for (const p of placements) {
    const other = getObject(p.defId);
    const ox = Math.abs(p.x - x);
    const oy = Math.abs(p.y - y);
    if (ox < hw + other.hitbox.w / 2 + 6 && oy < hh + other.hitbox.h / 2 + 6) return false;
  }
  const trial: Placement[] = [...placements, { defId, x, y }];
  const gap = largestGap(occupiedIntervals(trial, y, 28), COURSE_WIDTH);
  return gap >= MIN_GAP;
}

function tryPlace(
  rng: Rng,
  placements: Placement[],
  defId: string,
  y: number,
  preferX?: number,
): boolean {
  const xs: number[] = [];
  if (preferX !== undefined) xs.push(preferX);
  for (let i = 0; i < 10; i++) xs.push(randRange(rng, EDGE + 20, COURSE_WIDTH - EDGE - 20));
  for (const x of xs) {
    if (canPlace(placements, x, y, defId)) {
      const def = getObject(defId);
      placements.push({
        defId,
        x,
        y,
        variant: randInt(rng, 0, def.sprites.length - 1),
      });
      return true;
    }
  }
  return false;
}

// tryPlace falls back to ten random positions when the one it was asked for
// does not fit, which is right for scattering and wrong for a shape: a wall
// with a tree teleported into its opening is not a wall. This takes the x it
// was given or nothing at all.
function placeAt(rng: Rng, placements: Placement[], defId: string, y: number, x: number): boolean {
  if (!canPlace(placements, x, y, defId)) return false;
  const def = getObject(defId);
  placements.push({ defId, x, y, variant: randInt(rng, 0, def.sprites.length - 1) });
  return true;
}

type SegmentBuild = (rng: Rng, y0: number, difficulty: number, out: Placement[]) => void;

const SEGMENTS: { weight: (d: number) => number; build: SegmentBuild; height: number }[] = [
  {
    height: 340,
    weight: (d) => lerp(2.4, 1.4, d),
    build: (rng, y0, _d, out) => {
      if (chance(rng, 0.45)) tryPlace(rng, out, "rock", y0 + randRange(rng, 80, 260));
    },
  },
  {
    height: 360,
    weight: (d) => lerp(1.8, 1.2, d),
    build: (rng, y0, _d, out) => {
      tryPlace(rng, out, "tree", y0 + 160, chance(rng, 0.5) ? 90 : COURSE_WIDTH - 90);
      if (chance(rng, 0.5)) tryPlace(rng, out, "rock", y0 + 280);
    },
  },
  {
    height: 380,
    weight: (d) => lerp(0.6, 1.6, d),
    build: (rng, y0, d, out) => {
      const gap = lerp(150, 110, d);
      const mid = randRange(rng, 160, COURSE_WIDTH - 160);
      tryPlace(rng, out, "tree", y0 + 150, mid - gap / 2);
      tryPlace(rng, out, "tree", y0 + 150, mid + gap / 2);
    },
  },
  {
    height: 400,
    weight: (d) => lerp(0.8, 1.8, d),
    build: (rng, y0, d, out) => {
      const n = 3 + (d > 0.5 ? 1 : 0);
      let side = chance(rng, 0.5) ? -1 : 1;
      for (let i = 0; i < n; i++) {
        const x = COURSE_WIDTH / 2 + side * randRange(rng, 70, 150);
        tryPlace(rng, out, "tree", y0 + 70 + i * 90, x);
        side *= -1;
      }
    },
  },
  {
    height: 380,
    weight: (d) => lerp(1.0, 1.4, d),
    build: (rng, y0, d, out) => {
      const n = randInt(rng, 3, d > 0.6 ? 5 : 4);
      for (let i = 0; i < n; i++) {
        tryPlace(rng, out, "rock", y0 + 60 + i * 70);
      }
    },
  },
  {
    height: 460,
    weight: (d) => lerp(0.25, 0.5, d),
    build: (rng, y0, _d, out) => {
      const jx = randRange(rng, 140, COURSE_WIDTH - 140);
      tryPlace(rng, out, "jump", y0 + 80, jx);
      tryPlace(rng, out, "rock", y0 + 250, jx + randRange(rng, -90, 90));
      tryPlace(rng, out, "rock", y0 + 330, jx + randRange(rng, -120, 120));
      if (chance(rng, 0.4))
        tryPlace(rng, out, "tree", y0 + 380, jx < COURSE_WIDTH / 2 ? COURSE_WIDTH - 90 : 90);
    },
  },
  {
    height: 440,
    weight: (d) => lerp(0.4, 1.3, d),
    build: (rng, y0, d, out) => {
      const gap = lerp(140, 108, d);
      const mid = COURSE_WIDTH / 2 + randRange(rng, -40, 40);
      tryPlace(rng, out, "tree", y0 + 120, mid - gap);
      tryPlace(rng, out, "tree", y0 + 120, mid + gap);
      tryPlace(rng, out, "rock", y0 + 260, mid);
      tryPlace(rng, out, "tree", y0 + 360, mid - gap * 0.7);
      tryPlace(rng, out, "tree", y0 + 360, mid + gap * 0.7);
    },
  },
  {
    height: 420,
    weight: (d) => lerp(0.2, 0.4, d),
    build: (rng, y0, _d, out) => {
      tryPlace(rng, out, "jump", y0 + 70, COURSE_WIDTH / 2 + randRange(rng, -80, 80));
      const side = chance(rng, 0.5) ? 1 : -1;
      tryPlace(rng, out, "tree", y0 + 240, COURSE_WIDTH / 2 + side * 120);
      tryPlace(rng, out, "tree", y0 + 320, COURSE_WIDTH / 2 - side * 110);
    },
  },
  {
    height: 360,
    weight: (d) => lerp(0.3, 1.1, d),
    build: (rng, y0, d, out) => {
      const pinch = lerp(130, 100, d);
      const mid = randRange(rng, 180, COURSE_WIDTH - 180);
      tryPlace(rng, out, "tree", y0 + 100, mid - pinch);
      tryPlace(rng, out, "tree", y0 + 180, mid + pinch);
      tryPlace(rng, out, "rock", y0 + 260, mid + randRange(rng, -50, 50));
    },
  },

  // Everything above asks the same question in nine costumes: find the gap and
  // steer into it. The four below each ask something the others cannot.

  // Ramp gate -- the ramp is the route, not a bonus. A wall with its opening
  // deliberately far off the ramp's line: fly it, or make a long lateral
  // commitment to reach the gap on the ground. The opening stays honestly
  // rideable, so this is a strong preference rather than a toll gate.
  {
    height: 470,
    weight: (d) => lerp(0.3, 0.8, d),
    build: (rng, y0, d, out) => {
      const left = chance(rng, 0.5);
      const jx = left
        ? randRange(rng, 110, 165)
        : randRange(rng, COURSE_WIDTH - 165, COURSE_WIDTH - 110);
      const gx = left ? COURSE_WIDTH - randRange(rng, 95, 135) : randRange(rng, 95, 135);
      placeAt(rng, out, "jump", y0 + 90, jx);
      const half = lerp(80, 62, d);
      for (let x = EDGE + 26; x < COURSE_WIDTH - EDGE - 20; x += 56) {
        if (Math.abs(x - gx) < half) continue;
        placeAt(rng, out, "tree", y0 + 330, x);
      }
    },
  },

  // The drift -- the only segment where arriving fast is a liability. A gate
  // whose centre slides steadily across the hill, sized off the rider's own
  // lateral reach at the clamp: makeable at full speed only by committing
  // immediately and never drifting back. Arriving badly positioned costs the
  // segment; it is never an unavoidable hit.
  {
    height: 560,
    weight: (d) => lerp(0.4, 1.15, d),
    build: (rng, y0, d, out) => {
      const rows = 6;
      const gateHalf = lerp(86, 68, d);
      const minC = EDGE + gateHalf + 16;
      const maxC = COURSE_WIDTH - EDGE - gateHalf - 16;
      const span = Math.min(REACH_PER_UNIT * 560 * lerp(0.55, 0.85, d), maxC - minC);
      const dir = chance(rng, 0.5) ? 1 : -1;
      const start = dir > 0 ? randRange(rng, minC, maxC - span) : randRange(rng, minC + span, maxC);
      for (let i = 0; i < rows; i++) {
        const centre = start + dir * span * (i / (rows - 1));
        const y = y0 + 60 + i * 84;
        placeAt(rng, out, "tree", y, centre - gateHalf);
        placeAt(rng, out, "tree", y, centre + gateHalf);
      }
    },
  },

  // The fork -- a choice between two routes rather than a search for one gap.
  // A plug down the middle opens a wide safe lane and a tight one; the tight
  // side carries the ramp, and pinches again on the way out.
  {
    height: 480,
    weight: (d) => lerp(0.3, 0.8, d),
    build: (rng, y0, d, out) => {
      const mid = COURSE_WIDTH / 2 + randRange(rng, -30, 30);
      const plugHalf = lerp(52, 68, d);
      placeAt(rng, out, "tree", y0 + 110, mid - plugHalf * 0.5);
      placeAt(rng, out, "tree", y0 + 110, mid + plugHalf * 0.5);
      placeAt(rng, out, "rock", y0 + 200, mid);
      const tight = chance(rng, 0.5) ? -1 : 1;
      placeAt(rng, out, "jump", y0 + 300, mid + tight * (plugHalf + 76));
      placeAt(rng, out, "tree", y0 + 390, mid + tight * (plugHalf + 8));
    },
  },

  // Ramp chain -- hold one line for 400 units. Chaining ramps is already how a
  // good rider reaches the clamp, but every jump segment until now was
  // isolated, so nothing ever rewarded it. The rock sits off the far end of the
  // chain, so drifting wide of the line costs something.
  {
    height: 440,
    weight: (d) => lerp(0.2, 0.45, d),
    build: (rng, y0, d, out) => {
      const n = d > 0.5 ? 3 : 2;
      const dir = chance(rng, 0.5) ? 1 : -1;
      const stepX = randRange(rng, 26, 52);
      let x = COURSE_WIDTH / 2 - dir * stepX * (n - 1) * 0.5 + randRange(rng, -50, 50);
      for (let i = 0; i < n; i++) {
        placeAt(rng, out, "jump", y0 + 70 + i * 130, x);
        x += dir * stepX;
      }
      placeAt(rng, out, "rock", y0 + 30 + n * 130, x + dir * 90);
    },
  },
];

function pickSegment(rng: Rng, difficulty: number) {
  let total = 0;
  const weights = SEGMENTS.map((s) => {
    const w = s.weight(difficulty);
    total += w;
    return w;
  });
  let roll = rng() * total;
  for (let i = 0; i < SEGMENTS.length; i++) {
    roll -= weights[i]!;
    if (roll <= 0) return SEGMENTS[i]!;
  }
  return SEGMENTS[0]!;
}

// Removes the solid nearest `focus` at y. Which x to aim at is the whole
// difference between the two passes: widening a gap means clearing the middle
// of the course, but restoring reachability means clearing whatever stands
// between the rider's band and the open lane, which is rarely the middle.
function dropNearest(placements: Placement[], y: number, focus: number): boolean {
  let worst = -1;
  let worstDistance = Infinity;
  for (let i = 0; i < placements.length; i++) {
    const p = placements[i]!;
    if (Math.abs(p.y - y) > 28) continue;
    if (getObject(p.defId).collision !== "solid") continue;
    const distance = Math.abs(p.x - focus);
    if (distance < worstDistance) {
      worstDistance = distance;
      worst = i;
    }
  }
  if (worst < 0) return false;
  placements.splice(worst, 1);
  return true;
}

function ensureFairness(placements: Placement[]): void {
  for (let y = START_PAD; y < TRACK_LENGTH; y += 32) {
    let guard = 0;
    while (largestGap(occupiedIntervals(placements, y, 26), COURSE_WIDTH) < MIN_GAP && guard < 12) {
      guard += 1;
      if (!dropNearest(placements, y, COURSE_WIDTH / 2)) break;
    }
  }
}

// The rideable lanes at y: the complement of the solids, keeping only the runs
// a rider actually fits through.
function freeIntervals(placements: Placement[], y: number): [number, number][] {
  const out: [number, number][] = [];
  let cursor = EDGE;
  for (const [l, r] of occupiedIntervals(placements, y, 26)) {
    if (l - cursor >= MIN_GAP) out.push([cursor, l]);
    cursor = Math.max(cursor, r);
  }
  if (COURSE_WIDTH - EDGE - cursor >= MIN_GAP) out.push([cursor, COURSE_WIDTH - EDGE]);
  return out;
}

function intersect(a: [number, number][], b: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    const lo = Math.max(a[i]![0], b[j]![0]);
    const hi = Math.min(a[i]![1], b[j]![1]);
    if (hi - lo >= MIN_GAP) out.push([lo, hi]);
    if (a[i]![1] < b[j]![1]) i++;
    else j++;
  }
  return out;
}

// Lateral reach per unit of descent, at the speed where it is worst. Steering
// runs at STEER_BASE + speed * STEER_SPEED_GAIN while descent runs at speed
// itself, so the ratio falls as the rider speeds up: 2.14px of sideways travel
// per unit of descent at MIN_SPEED, but only 0.49 at MAX_SPEED. Checking the
// clamp checks every slower case with it.
const REACH_PER_UNIT = (STEER_BASE + MAX_SPEED * STEER_SPEED_GAIN) / MAX_SPEED;

// ensureFairness proves a gap exists. It does not prove the rider can be in it.
// A course can hand you a legal gap on the far side of the hill from the one
// you are in and still pass, which at speed is indistinguishable from the
// generator being unfair. This carries the reachable band forward, widening it
// by how far a rider could physically steer in each step and clipping it to the
// lanes that are actually open, and thins obstacles wherever that band dies.
function ensureReachable(placements: Placement[]): void {
  const step = 32;
  const spread = REACH_PER_UNIT * step;
  const full: [number, number][] = [[EDGE, COURSE_WIDTH - EDGE]];
  let reach = full;

  for (let y = START_PAD; y < TRACK_LENGTH; y += step) {
    const grown = reach.map(([l, r]) => [l - spread, r + spread] as [number, number]);
    let next = intersect(grown, freeIntervals(placements, y));
    let guard = 0;
    while (next.length === 0 && guard < 24) {
      guard += 1;
      // Aim at the middle of the band the rider could actually be holding.
      let focus = COURSE_WIDTH / 2;
      let widest = -Infinity;
      for (const [l, r] of grown) {
        if (r - l > widest) {
          widest = r - l;
          focus = (l + r) / 2;
        }
      }
      if (!dropNearest(placements, y, focus)) break;
      next = intersect(grown, freeIntervals(placements, y));
    }
    // A band that still cannot be saved would poison every row after it, so
    // pick the run back up from whatever is open here.
    if (next.length === 0) next = freeIntervals(placements, y);
    reach = next.length === 0 ? full : next;
  }
}

// How far up the hollow wall the forest reaches. Wider than the strip the
// phone view shows: a desktop viewport is height-fit and sees well past the
// old 150, where the forest used to stop dead at an invisible line. The
// ground shader's deep-woods band (uBank * 2.2 in src/game/gl/shaders.ts)
// reaches full strength at this same distance -- retune them together.
const BANK_INNER = 240;

// The banks used to be a hedge: two to four trees every row, one narrow scale
// band, flip the only variety. A forest has structure, so each bank gets a
// slow density wave down the descent -- thickets, and true clearings where a
// row plants nothing -- plus occasional elder trees a size class up, roadside
// boulders near the lane edge, and a rise with distance from the lane so the
// treeline reads as climbing the hollow wall rather than standing on a stripe.
function plantScenery(rng: Rng, length: number): Scenery[] {
  const out: Scenery[] = [];
  const trees = ["tree-pine", "tree-hemlock"] as const;
  const rocks = ["rock-0", "rock-1", "rock-2", "rock-3"] as const;
  // Independent phases so the two banks thin and thicken out of step; a
  // clearing on both sides at once reads as a gate the course did not build.
  const phaseL = rng() * Math.PI * 2;
  const phaseR = rng() * Math.PI * 2;
  for (let y = -200; y < length + 500; y += randRange(rng, 42, 84)) {
    for (const side of [-1, 1] as const) {
      const density =
        0.5 + 0.5 * Math.sin(y * 0.0031 + (side < 0 ? phaseL : phaseR)) + randRange(rng, -0.2, 0.2);
      const n = density < 0.14 ? 0 : randInt(rng, 1, 2 + Math.round(3 * Math.min(1, density)));
      for (let i = 0; i < n; i++) {
        const depth = randRange(rng, 0, 1);
        const inset = 18 + depth * (BANK_INNER - 18);
        const rise = 0.8 + 0.45 * depth;
        const elder = chance(rng, 0.07);
        out.push({
          x: side < 0 ? -inset : COURSE_WIDTH + inset,
          y: y + randRange(rng, -22, 22),
          sprite: pick(rng, trees),
          scale: randRange(rng, 0.7, 1.15) * rise * (elder ? 1.3 : 1),
          flip: chance(rng, 0.5),
        });
      }
      if (chance(rng, 0.08)) {
        const inset = randRange(rng, 20, 70);
        out.push({
          x: side < 0 ? -inset : COURSE_WIDTH + inset,
          y: y + randRange(rng, -18, 18),
          sprite: pick(rng, rocks),
          scale: randRange(rng, 0.45, 0.75),
          flip: chance(rng, 0.5),
        });
      }
    }
  }
  return out;
}

// Roughly one pickup per this much descent. Spacing rather than a fixed count,
// so the budget follows the track length instead of thinning out when it grows.
const PICKUP_SPACING = 560;

// One in five is a burger; the rest are hot dogs.
const BURGER_EVERY = 5;

// How far off the middle of the free lane a pickup sits, as a fraction of the
// half-gap. Steering costs no speed in this game, so a pickup sitting on the
// racing line would be free -- the whole choice is manufactured by putting it
// far enough to one side that taking it costs a lateral commitment, which gets
// more expensive the faster you are going.
const PICKUP_OFFSET = 0.62;

// How far up and down the hill to look for solids when choosing a lane. Has to
// cover the tallest hitbox pair that could overlap -- a 46-tall pickup against a
// 36-tall tree is 41 units -- or food lands on top of a tree.
const PICKUP_BAND = 64;

// Placed after ensureFairness, in its own RNG stream, so the obstacle layout of
// every existing seed is byte-identical to what it was before pickups existed.
function plantPickups(rng: Rng, placements: Placement[]): Placement[] {
  const food: Placement[] = [];
  const end = TRACK_LENGTH - FINISH_CLEAR;
  let placed = 0;

  for (let y = START_PAD + PICKUP_SPACING; y < end; y += PICKUP_SPACING) {
    const at = y + randRange(rng, -90, 90);
    if (at <= START_PAD || at >= end) continue;

    const [lo, hi] = widestGap(occupiedIntervals(placements, at, PICKUP_BAND), COURSE_WIDTH);
    const mid = (lo + hi) / 2;
    const half = (hi - lo) / 2;
    const defId = placed % BURGER_EVERY === BURGER_EVERY - 1 ? "burger" : "hotdog";
    const reach = half - getObject(defId).hitbox.w / 2 - 6;
    if (reach <= 0) continue;

    const side = chance(rng, 0.5) ? -1 : 1;
    const x = mid + side * Math.min(reach, half * PICKUP_OFFSET);
    food.push({ defId, x, y: at });
    placed += 1;
  }

  return food;
}

export function generateCourse(rng: Rng, sceneryRng: Rng, pickupRng: Rng): Course {
  nextUid = 1;
  const placements: Placement[] = [];
  let y = START_PAD;
  const end = TRACK_LENGTH - FINISH_CLEAR;

  while (y < START_PAD + 520) {
    if (chance(rng, 0.4)) tryPlace(rng, placements, "rock", y + 80);
    y += 260;
  }

  while (y < end) {
    const difficulty = Math.max(0, Math.min(1, (y - START_PAD) / RAMP_DISTANCE));
    const seg = pickSegment(rng, difficulty);
    seg.build(rng, y, difficulty, placements);
    y += seg.height;
  }

  ensureFairness(placements);
  ensureReachable(placements);
  placements.push(...plantPickups(pickupRng, placements));

  const entities: Entity[] = placements.map((p) => ({
    uid: nextUid++,
    defId: p.defId,
    x: p.x,
    y: p.y,
    variant: p.variant ?? 0,
    consumed: false,
    ignoreUntil: 0,
  }));

  return {
    entities,
    scenery: plantScenery(sceneryRng, TRACK_LENGTH),
    length: TRACK_LENGTH,
    width: COURSE_WIDTH,
  };
}
